/**
 * Fordonsrapport — export enligt synliga kolumner i fordonslistan.
 */

import vehiclesService, { type Vehicle } from '@/services/vehicles/vehiclesService';
import type { DataTableColumn } from '@/components/common/DataTable';
import type { StatusFilter, ConnectedFilter, PresetFilter } from '@/types/fleet';
import { exportTableToCsv, exportTableToExcel } from '@/utils/tableExport';
import { FLEET_COLUMN_FIXED_LAST } from '@/types/fleetColumnPreferences';

const EXPORT_PAGE_SIZE = 5000;

export function getFleetReportExportColumns<T extends object>(
  columns: DataTableColumn<T>[]
): DataTableColumn<T>[] {
  return columns.filter((c) => c.key !== FLEET_COLUMN_FIXED_LAST);
}

export async function fetchAllFilteredFleetVehicles(opts: {
  search: string;
  statusFilter: StatusFilter;
  organizationFilter: string;
  carInfoFilterIds: number[] | null;
  connectedFilter: ConnectedFilter;
  presetFilter?: PresetFilter;
  usageStatusId?: string;
}): Promise<Vehicle[]> {
  const baseParams = {
    limit: EXPORT_PAGE_SIZE,
    search: opts.search || undefined,
    status: opts.statusFilter || undefined,
    organizationId: opts.organizationFilter || undefined,
    includeSubOrgs: true,
    vehicleIds: opts.carInfoFilterIds || undefined,
    connected: opts.connectedFilter || undefined,
    presetFilter: opts.presetFilter || undefined,
    usageStatusId: opts.usageStatusId || undefined,
  };

  const first = await vehiclesService.getVehicles({ ...baseParams, page: 1 });
  let all = [...first.vehicles];

  if (first.pagination.totalPages > 1) {
    for (let page = 2; page <= first.pagination.totalPages; page += 1) {
      const next = await vehiclesService.getVehicles({ ...baseParams, page });
      all = all.concat(next.vehicles);
    }
  }

  if (opts.carInfoFilterIds !== null) {
    const idSet = new Set(opts.carInfoFilterIds);
    all = all.filter((v) => idSet.has(parseInt(v.id, 10)));
  }

  return all;
}

export async function downloadFleetGridReportExport<T extends object>(opts: {
  format: 'xlsx' | 'csv';
  columns: DataTableColumn<T>[];
  data: T[];
}): Promise<void> {
  const exportColumns = getFleetReportExportColumns(opts.columns);
  const stamp = new Date().toISOString().slice(0, 10);
  const filename =
    opts.format === 'csv' ? `fordonsrapport_${stamp}.csv` : `fordonsrapport_${stamp}.xlsx`;

  if (opts.format === 'csv') {
    exportTableToCsv(exportColumns, opts.data, filename);
    return;
  }

  await exportTableToExcel(exportColumns, opts.data, filename, 'Fordonsrapport');
}
